import { Button, cn } from '@sib/ui';
import { ArrowRight } from '@sib/ui/icons';
import { useUIContext } from '../../UIContext';
import { SidebarItem } from './SidebarItem';
import type { SidebarMenuItem } from './Sidebar.utils';

interface SidebarProps {
  onChange: (state: boolean) => void;
  open: boolean;
  hidden?: boolean;
  applicationName?: string;
  applicationVersion?: string;
  items: SidebarMenuItem[];
  header?: React.ReactNode;
  footer?: React.ReactNode;
  fullWidth?: boolean;
  hideArrowButton?: boolean;
}

export const Sidebar = ({
  onChange,
  open,
  applicationName,
  applicationVersion,
  header,
  footer,
  items: sidebarItems,
  hidden = false,
  fullWidth = false,
  hideArrowButton = false,
}: SidebarProps) => {
  const { submenuOpen, setSubmenuOpen } = useUIContext();

  return (
    <aside
      className={cn(
        'transition-width z-20 flex h-full flex-col overflow-x-hidden overflow-y-auto border-r bg-white duration-400',
        hidden
          ? 'w-0 overflow-x-visible'
          : open
            ? fullWidth
              ? 'w-full'
              : 'w-64'
            : 'w-13',
        fullWidth && 'border-r-0'
      )}
    >
      <nav
        className={cn(
          'flex w-64 flex-[1_1] flex-col border-r',
          fullWidth && 'w-full border-r-0'
        )}
      >
        {header}
        {sidebarItems.map(item => (
          <SidebarItem
            key={`sidebar-item-${item.title}`}
            {...item}
            open={open}
            isExpandedSubmenu={item.hasExpandSubmenuArrow && submenuOpen}
            onItemClick={hasSubmenu => {
              if (!hasSubmenu) return setSubmenuOpen(false);
              setSubmenuOpen(!submenuOpen);
            }}
          />
        ))}
        {footer}
      </nav>
      <div className="flex w-full flex-col items-center gap-1 pb-2">
        {!hideArrowButton && (
          <Button
            className={cn('flex w-full items-center justify-end')}
            variant="ghost"
            onClick={() => onChange(!open)}
          >
            <ArrowRight
              className={cn(
                'min-size-4.5 transition-transform duration-300',
                open && 'rotate-y-180'
              )}
            />
          </Button>
        )}
        <p className="text-muted-foreground text-2xs flex flex-nowrap items-center justify-center gap-0.5">
          {applicationName && (
            <span
              className={cn(
                'overflow-hidden whitespace-nowrap transition-all duration-300',
                open ? 'max-w-30' : 'max-w-0'
              )}
            >
              {applicationName}
            </span>
          )}
          {applicationVersion && (
            <span
              className={cn(
                'overflow-hidden whitespace-nowrap transition-all duration-300',
                open ? 'max-w-30' : 'max-w-0'
              )}
            >
              {applicationVersion}
            </span>
          )}
        </p>
      </div>
    </aside>
  );
};
